import { influencer, InstagramUserImage, TiktokUserImage } from '@round/api';
import { useInstagramUserImageQueriesByIds } from '@round/platforms/instagram';
import { useTiktokUserImageQueriesByIds } from '@round/platforms/tiktok';

/** Rows grouped by the request that loaded them: images are fetched one query per group. */
export function useCreatorImages(creatorGroups: influencer.CreatorSearchResult[][]) {
    const tiktokImageQueries = useTiktokUserImageQueriesByIds(
        creatorGroups.map((creators) =>
            creators.map((creator) => creator.platforms.tiktok[0]?.creatorbase_user_id).filter(Boolean)
        )
    );

    const instagramImageQueries = useInstagramUserImageQueriesByIds(
        creatorGroups.map((creators) =>
            creators.map((creator) => creator.platforms.instagram[0]?.creatorbase_user_id).filter(Boolean)
        )
    );

    const getCreatorImages = (creator: influencer.CreatorSearchResult | {} | null | undefined) => {
        if (!creator || !('platforms' in creator)) {
            return {
                tiktok: null,
                instagram: null,
            };
        }

        let tiktokImage: TiktokUserImage | undefined;
        let instagramImage: InstagramUserImage | undefined;

        if (creator.platforms.tiktok.length > 0) {
            for (const query of tiktokImageQueries.flat()) {
                const image = query.data?.find(
                    (result) => result.user_id === creator.platforms.tiktok[0]?.creatorbase_user_id
                );
                if (image) {
                    tiktokImage = image;
                    break;
                }
            }
        }

        if (creator.platforms.instagram.length > 0) {
            for (const query of instagramImageQueries.flat()) {
                const image = query.data?.find(
                    (result) => result.user_id === creator.platforms.instagram[0]?.creatorbase_user_id
                );
                if (image) {
                    instagramImage = image;
                    break;
                }
            }
        }

        return {
            tiktok: tiktokImage?.avatar_thumb.cached_url ?? tiktokImage?.avatar_thumb.original_url,
            instagram: instagramImage?.avatar_thumb.cached_url ?? instagramImage?.avatar_thumb.original_url,
        };
    };

    const getAreCreatorImagesLoading = (creator: influencer.CreatorSearchResult) => {
        const groupIndex = creatorGroups.findIndex((creators) => creators.some((result) => result._id === creator._id));

        if (groupIndex === -1) {
            return false;
        }

        return tiktokImageQueries[groupIndex]?.isLoading || instagramImageQueries[groupIndex]?.isLoading;
    };

    return { getCreatorImages, getAreCreatorImagesLoading };
}
